import type { BrandSettings, PriceItem, ReportContent, TemplateDoc } from '@quickreport/domain';
import { sql } from 'drizzle-orm';
import {
  bigint,
  boolean,
  customType,
  date,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';

/**
 * One schema for the server (Postgres 18) and every client (PGlite in the browser and
 * Electron). Rules:
 * - ids are UUIDv7 generated by the client, so rows can be created offline;
 * - every synced table carries org_id, rev (assigned by the server on sync), timestamps
 *   and a soft-delete marker;
 * - nothing here uses features PGlite lacks;
 * - no foreign keys: during sync, rows may arrive in any order. Integrity is checked by the
 *   services that write them.
 */

const ts = (name: string) => timestamp(name, { withTimezone: true, mode: 'string' });

const synced = {
  id: uuid('id').primaryKey(),
  orgId: uuid('org_id').notNull(),
  /** Server-assigned, per-organization, strictly increasing. 0 until the server has it. */
  rev: bigint('rev', { mode: 'number' }).notNull().default(0),
  createdAt: ts('created_at').notNull().default(sql`now()`),
  updatedAt: ts('updated_at').notNull().default(sql`now()`),
  deletedAt: ts('deleted_at'),
  /** Device that made the last change; lets sync skip echoing a device's own writes. */
  updatedBy: uuid('updated_by'),
};

export const siteType = pgEnum('site_type', ['processing', 'collection']);
export const workflowLevel = pgEnum('workflow_level', ['simple', 'standard', 'nabl']);
export const reportStatus = pgEnum('report_status', [
  'draft',
  'awaiting_verification',
  'awaiting_signature',
  'partial',
  'final',
  'amended',
  'revoked',
]);
export const sexEnum = pgEnum('sex', ['M', 'F', 'O']);

export const organizations = pgTable('organizations', {
  id: uuid('id').primaryKey(),
  name: text('name').notNull(),
  createdAt: ts('created_at').notNull().default(sql`now()`),
  updatedAt: ts('updated_at').notNull().default(sql`now()`),
});

export const sites = pgTable(
  'sites',
  {
    ...synced,
    type: siteType('type').notNull().default('processing'),
    /** Short code used in lab numbers, e.g. SXR. */
    code: text('code').notNull(),
    name: text('name').notNull(),
    workflowLevel: workflowLevel('workflow_level').notNull().default('simple'),
    labNumberPattern: text('lab_number_pattern').notNull().default('{YYYY}-{seq:6}'),
  },
  (t) => [uniqueIndex('sites_org_code').on(t.orgId, t.code)],
);

export const members = pgTable(
  'members',
  {
    ...synced,
    /** Auth user id (Better Auth uses text ids). */
    userId: text('user_id').notNull(),
    siteId: uuid('site_id'),
    displayName: text('display_name').notNull(),
    roles: text('roles').array().notNull().default(sql`'{}'::text[]`),
  },
  (t) => [index('members_user').on(t.userId)],
);

export const devices = pgTable('devices', {
  ...synced,
  siteId: uuid('site_id').notNull(),
  name: text('name').notNull(),
  platform: text('platform').notNull(),
  lastSeenAt: ts('last_seen_at'),
  revokedAt: ts('revoked_at'),
});

/** Lab numbers reserved by a device so it can number reports while offline. */
export const numberBlocks = pgTable('number_blocks', {
  ...synced,
  siteId: uuid('site_id').notNull(),
  deviceId: uuid('device_id').notNull(),
  year: integer('year').notNull(),
  first: integer('first').notNull(),
  last: integer('last').notNull(),
  next: integer('next').notNull(),
});

/** Organization defaults (site_id null) and per-site overrides. */
export const brandSettings = pgTable(
  'brand_settings',
  {
    ...synced,
    siteId: uuid('site_id'),
    doc: jsonb('doc').$type<BrandSettings>().notNull(),
  },
  (t) => [unique('brand_settings_scope').on(t.orgId, t.siteId).nullsNotDistinct()],
);

export const signatories = pgTable('signatories', {
  ...synced,
  siteId: uuid('site_id'),
  memberId: uuid('member_id'),
  name: text('name').notNull(),
  qualification: text('qualification').notNull(),
  designation: text('designation'),
  regNo: text('reg_no'),
  signatureFileId: uuid('signature_file_id'),
  departments: text('departments').array().notNull().default(sql`'{}'::text[]`),
});

export const files = pgTable('files', {
  ...synced,
  sha256: text('sha256').notNull(),
  mime: text('mime').notNull(),
  size: integer('size').notNull(),
  /** Object key on the server; null on clients until uploaded. */
  storageKey: text('storage_key'),
});

export const folders = pgTable('folders', {
  ...synced,
  parentId: uuid('parent_id'),
  name: text('name').notNull(),
});

export const templates = pgTable(
  'templates',
  {
    ...synced,
    siteId: uuid('site_id'),
    /** Set on a site override; points at the organization master. */
    masterId: uuid('master_id'),
    name: text('name').notNull(),
    /** Name + aliases + department + tags, lower-cased, for search. */
    searchText: text('search_text').notNull().default(''),
    department: text('department'),
    currentVersion: integer('current_version').notNull().default(1),
    sensitive: boolean('sensitive').notNull().default(false),
    archivedAt: ts('archived_at'),
  },
  (t) => [index('templates_org').on(t.orgId), index('templates_org_name').on(t.orgId, t.name)],
);

export const templateVersions = pgTable(
  'template_versions',
  {
    ...synced,
    templateId: uuid('template_id').notNull(),
    version: integer('version').notNull(),
    doc: jsonb('doc').$type<TemplateDoc>().notNull(),
  },
  (t) => [uniqueIndex('template_versions_unique').on(t.templateId, t.version)],
);

export const patients = pgTable(
  'patients',
  {
    ...synced,
    salutation: text('salutation'),
    name: text('name').notNull(),
    sex: sexEnum('sex'),
    dob: date('dob', { mode: 'string' }),
    /** Age as typed when no date of birth is known, with the date it was recorded. */
    ageDays: integer('age_days'),
    ageRecordedOn: date('age_recorded_on', { mode: 'string' }),
    /** E.164. Not unique: families share numbers. */
    phone: text('phone'),
    email: text('email'),
    address: text('address'),
    externalId: text('external_id'),
    extra: jsonb('extra').$type<Record<string, string>>().notNull().default({}),
  },
  (t) => [
    index('patients_phone').on(t.orgId, t.phone),
    index('patients_name').on(t.orgId, t.name),
    index('patients_org_created').on(t.orgId, t.createdAt),
  ],
);

export const visits = pgTable(
  'visits',
  {
    ...synced,
    siteId: uuid('site_id').notNull(),
    patientId: uuid('patient_id').notNull(),
    visitNo: text('visit_no').notNull(),
    referredBy: text('referred_by'),
    collectedAt: ts('collected_at'),
    receivedAt: ts('received_at'),
    /** Code printed on the counter slip for status tracking; issued by the server. */
    trackCode: text('track_code'),
  },
  (t) => [
    index('visits_patient').on(t.patientId),
    uniqueIndex('visits_track_code').on(t.trackCode),
  ],
);

export const reports = pgTable(
  'reports',
  {
    ...synced,
    siteId: uuid('site_id').notNull(),
    visitId: uuid('visit_id').notNull(),
    folderId: uuid('folder_id'),
    name: text('name').notNull(),
    labNo: text('lab_no').notNull(),
    status: reportStatus('status').notNull().default('draft'),
    /** Working copy while not final. Signed versions live in report_versions. */
    content: jsonb('content').$type<ReportContent>().notNull(),
    version: integer('version').notNull().default(0),
    searchText: text('search_text').notNull().default(''),
    /** Issued by the server after sync; until then the PDF carries no QR. */
    verifyCode: text('verify_code'),
    sensitive: boolean('sensitive').notNull().default(false),
    /** Copied from content.visit so lists can sort and filter on them with an index. */
    collectedAt: ts('collected_at'),
    receivedAt: ts('received_at'),
    /**
     * What the lab charges, in paise, fixed when the tests were chosen. Kept here and not in
     * `content`: it is private to the lab, never printed, and can change after signing.
     */
    pricePaise: integer('price_paise').notNull().default(0),
    discountPaise: integer('discount_paise').notNull().default(0),
    /** Each test and its price at that time. */
    priceItems: jsonb('price_items').$type<PriceItem[]>().notNull().default(sql`'[]'::jsonb`),
    /** The day the lab promised the report (YYYY-MM-DD). Drives the due-today reminders. */
    dueDate: date('due_date', { mode: 'string' }),
    /** Set when the lab marks the reminder complete without waiting for a signature. */
    dueDoneAt: ts('due_done_at'),
  },
  (t) => [
    index('reports_org_updated').on(t.orgId, t.updatedAt),
    index('reports_org_due').on(t.orgId, t.dueDate),
    index('reports_org_collected').on(t.orgId, t.collectedAt),
    index('reports_org_received').on(t.orgId, t.receivedAt),
    index('reports_visit').on(t.visitId),
    index('reports_lab_no').on(t.orgId, t.labNo),
    uniqueIndex('reports_verify_code').on(t.verifyCode),
  ],
);

/** Money received for a report. Several rows when a patient pays in parts; a mistake is removed (soft delete) and re-entered. */
export const payments = pgTable(
  'payments',
  {
    ...synced,
    reportId: uuid('report_id').notNull(),
    amountPaise: integer('amount_paise').notNull(),
    method: text('method').notNull().default('cash'),
    paidAt: ts('paid_at').notNull().default(sql`now()`),
    note: text('note'),
  },
  (t) => [
    index('payments_report').on(t.reportId),
    index('payments_org_paid').on(t.orgId, t.paidAt),
  ],
);

/** Sample types the lab typed in beyond Blood, Urine and Semen, offered again next time. */
export const sampleTypes = pgTable(
  'sample_types',
  {
    ...synced,
    name: text('name').notNull(),
  },
  (t) => [index('sample_types_org').on(t.orgId)],
);

/** Money the lab spent: rent, reagents, salaries. Private to the lab, with optional bill photos. */
export const expenses = pgTable(
  'expenses',
  {
    ...synced,
    /** The day it was spent (YYYY-MM-DD). */
    spentOn: date('spent_on', { mode: 'string' }).notNull(),
    amountPaise: integer('amount_paise').notNull(),
    category: text('category').notNull().default('Other'),
    /** Who was paid. */
    vendor: text('vendor'),
    method: text('method').notNull().default('cash'),
    note: text('note'),
    /** Photos or screenshots of the bill, as ids of rows in `files`. */
    fileIds: jsonb('file_ids').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  },
  (t) => [
    index('expenses_org_spent').on(t.orgId, t.spentOn),
    index('expenses_org_category').on(t.orgId, t.category),
  ],
);

export const reportVersions = pgTable(
  'report_versions',
  {
    ...synced,
    reportId: uuid('report_id').notNull(),
    version: integer('version').notNull(),
    content: jsonb('content').$type<ReportContent>().notNull(),
    signedBy: uuid('signed_by'),
    signedAt: ts('signed_at').notNull(),
    amendReason: text('amend_reason'),
    pdfFileId: uuid('pdf_file_id'),
    pdfSha256: text('pdf_sha256'),
  },
  (t) => [uniqueIndex('report_versions_unique').on(t.reportId, t.version)],
);

/** Append-only; written in the same transaction as the change it records. */
export const auditEvents = pgTable(
  'audit_events',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id').notNull(),
    actor: text('actor').notNull(),
    deviceId: uuid('device_id'),
    entity: text('entity').notNull(),
    entityId: uuid('entity_id').notNull(),
    action: text('action').notNull(),
    before: jsonb('before'),
    after: jsonb('after'),
    reason: text('reason'),
    at: ts('at').notNull().default(sql`now()`),
    rev: bigint('rev', { mode: 'number' }).notNull().default(0),
  },
  (t) => [index('audit_entity').on(t.entityId)],
);

/** Server-side counter that hands out `rev` values per organization. */
export const orgRevs = pgTable('org_revs', {
  orgId: uuid('org_id').primaryKey(),
  rev: bigint('rev', { mode: 'number' }).notNull().default(0),
});

/** Client-side queue of local changes waiting to be pushed. Never synced itself. */
export const outbox = pgTable(
  'outbox',
  {
    id: uuid('id').primaryKey(),
    /** Orders changes; a row's later entries supersede its earlier ones. */
    seq: bigint('seq', { mode: 'number' }).generatedAlwaysAsIdentity().notNull(),
    tableName: text('table_name').notNull(),
    rowId: uuid('row_id').notNull(),
    op: text('op').notNull(),
    baseRev: bigint('base_rev', { mode: 'number' }).notNull(),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    attempts: integer('attempts').notNull().default(0),
    lastError: text('last_error'),
  },
  (t) => [index('outbox_row').on(t.rowId)],
);

/**
 * Client-side: a local change that lost to a newer server version. Kept so the person can
 * see what was overwritten ("Changed on another device").
 */
export const syncConflicts = pgTable('sync_conflicts', {
  id: uuid('id').primaryKey(),
  tableName: text('table_name').notNull(),
  rowId: uuid('row_id').notNull(),
  localRow: jsonb('local_row').notNull(),
  serverRow: jsonb('server_row').notNull(),
  reason: text('reason').notNull().default('newer_on_server'),
  at: ts('at').notNull().default(sql`now()`),
  dismissedAt: ts('dismissed_at'),
});

/** Client-side bytes of images (logo, signatures) until they are uploaded. */
export const fileBlobs = pgTable('file_blobs', {
  id: uuid('id').primaryKey(),
  bytes: customType<{ data: Uint8Array; driverData: Uint8Array }>({
    dataType: () => 'bytea',
  })('bytes').notNull(),
  uploadedAt: ts('uploaded_at'),
});

/** Client-side record of how far this device has pulled. */
export const syncState = pgTable(
  'sync_state',
  {
    orgId: uuid('org_id').notNull(),
    key: text('key').notNull(),
    value: text('value').notNull(),
  },
  (t) => [primaryKey({ columns: [t.orgId, t.key] })],
);

/** Tables that sync between devices and the server, in dependency order. */
export const SYNCED_TABLES = [
  'sites',
  'members',
  'devices',
  'number_blocks',
  'brand_settings',
  'signatories',
  'files',
  'folders',
  'templates',
  'template_versions',
  'patients',
  'visits',
  'reports',
  'payments',
  'expenses',
  'sample_types',
  'report_versions',
  'audit_events',
] as const;
